import { useEffect, useState } from "react";
import { X, ArrowRight, Check } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import { isPromoActive } from "@/lib/promo";

const SESSION_KEY = "flv_exit_intent_seen_v2";

const ExitIntentPopup = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Pause the welcome popup while a site-wide promo is running to avoid the
    // impression that BIENVENUE10 and the promo stack. Auto-resumes at the end
    // of the promo window (see /app/frontend/src/lib/promo.js).
    if (isPromoActive()) return;
    let seen = false;
    try {
      seen = sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      /* noop */
    }
    if (seen) return;

    const onMouseOut = (e) => {
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY > 10) return;
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* noop */
      }
      setOpen(true);
      document.removeEventListener("mouseout", onMouseOut);
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, []);

  const close = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await subscribeNewsletter(email, lang);
      setUnlocked(true);
    } catch (_err) {
      toast.error(isFr ? "Email invalide" : "Invalid email");
    } finally {
      setSubmitting(false);
    }
  };

  const closeUnlocked = () => close();

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={close}
        aria-label={isFr ? "Fermer" : "Close"}
        className="absolute inset-0 bg-[#1A1A1A]/45 backdrop-blur-[2px]"
      />
      <div
        className="relative bg-[#F5EFE6] text-[#3D2B1F] max-w-md w-full rounded-md shadow-[0_30px_80px_-20px_rgba(61,43,31,0.45)] p-8 md:p-10"
        data-testid="exit-intent-card"
      >
        <button
          type="button"
          onClick={close}
          data-testid="exit-intent-close"
          aria-label={isFr ? "Fermer" : "Close"}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-[#ebe7df] text-[#3D2B1F]"
        >
          <X size={16} strokeWidth={1.6} />
        </button>

        {unlocked ? (
          <>
            <div className="text-[#8a6d3b] text-3xl mb-3">
              <Check size={32} strokeWidth={1.6} />
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight mb-4">
              {isFr ? "Bienvenue dans le rituel." : "Welcome to the ritual."}
            </h2>
            <p className="text-[15px] leading-relaxed text-[#3D2B1F]/85 mb-6">
              {isFr
                ? "Merci ! Votre code de bienvenue vient d'être envoyé sur votre email. Il vous attend dans votre boîte mail (vérifiez aussi vos spams)."
                : "Thank you! Your welcome code has just been sent to your inbox. Check your email (and spam folder just in case)."}
            </p>
            <button
              type="button"
              onClick={closeUnlocked}
              data-testid="exit-intent-cta"
              className="w-full bg-[#3D2B1F] text-white py-4 text-sm tracking-[0.22em] uppercase font-medium rounded-md hover:bg-[#1A1A1A] transition-colors inline-flex items-center justify-center gap-2"
            >
              {isFr ? "Continuer" : "Continue"}
              <ArrowRight size={14} strokeWidth={1.6} />
            </button>
          </>
        ) : (
          <>
            <div className="eyebrow mb-4 text-[#8a6d3b]">
              {isFr ? "Offre de bienvenue" : "Welcome offer"}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight mb-4">
              {isFr ? "-10% sur votre 1ère commande" : "10% off your 1st order"}
            </h2>
            <p className="text-[15px] leading-relaxed text-[#3D2B1F]/85 mb-6">
              {isFr
                ? "Avant de partir — laissez votre email et recevez votre code de bienvenue immédiatement."
                : "Before you go — leave your email and receive your welcome code instantly."}
            </p>
            <form onSubmit={handleSubmit} data-testid="exit-intent-form">
              <div className="flex items-center bg-white/60 border border-[#3D2B1F]/30 focus-within:border-[#3D2B1F] transition-colors rounded-sm overflow-hidden mb-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isFr ? "votre.email@exemple.com" : "your.email@example.com"}
                  data-testid="exit-intent-email"
                  className="flex-1 bg-transparent px-4 py-3.5 outline-none placeholder:text-[#4a4a4a]/70 text-[15px] text-[#3D2B1F]"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="exit-intent-submit"
                className="w-full bg-[#3D2B1F] text-white py-4 text-sm tracking-[0.22em] uppercase font-medium rounded-md hover:bg-[#1A1A1A] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
              >
                {submitting
                  ? (isFr ? "Envoi…" : "Sending…")
                  : (isFr ? "Recevoir mes -10%" : "Get my 10% off")}
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
              <p className="text-xs text-[#4a4a4a] mt-3 text-center">
                {isFr ? "Désinscription en 1 clic. Aucun spam." : "Unsubscribe in 1 click. No spam."}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
